// eslint-disable-next-line no-restricted-imports -- GitHub is a brand icon with no Rounded/Outlined variant
import GitHubIcon from "@mui/icons-material/GitHub";
import { Button, CircularProgress, Divider, Paper, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { logError } from "@entrilia/shared/logging";
import { wait } from "@entrilia/shared/utilities/promiseHelper";
import type { PollAuthResult, StartAuthResponse } from "../../../api/copilotAgentTypes";

interface GitHubAuthGateProps {
  agentDisplayName: string;
  auth: StartAuthResponse;
  onPollAuth: () => Promise<PollAuthResult>;
  onRestartAuth: () => Promise<void>;
}

const GitHubAuthGate = ({ agentDisplayName, auth, onPollAuth, onRestartAuth }: GitHubAuthGateProps) => {
  const [opened, setOpened] = useState(false);
  const [pollMessage, setPollMessage] = useState<string>();
  const [hasTerminalError, setHasTerminalError] = useState(false);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!opened) {
      return;
    }
    cancelledRef.current = false;
    const intervalMs = Math.max((auth.interval ?? 5) * 1000, 6000);

    const poll = async () => {
      while (!cancelledRef.current) {
        await wait(intervalMs);
        if (cancelledRef.current) {
          break;
        }
        try {
          const result = await onPollAuth();
          if (cancelledRef.current) {
            break;
          }
          setPollMessage(result.message);
          setHasTerminalError(!result.authorized && !result.continuePolling);
          if (result.authorized || !result.continuePolling) {
            break;
          }
        } catch (err) {
          logError(err, "[GitHubAuthGate] pollAuth");
          setPollMessage("Failed to check GitHub authorization. Start again to retry.");
          setHasTerminalError(true);
          break;
        }
      }
    };

    poll();
    return () => {
      cancelledRef.current = true;
    };
  }, [opened, auth.interval, onPollAuth]);

  const restartAuth = async () => {
    setPollMessage(undefined);
    setHasTerminalError(false);
    setOpened(false);
    await onRestartAuth();
  };

  return (
    <Stack sx={{ alignItems: "center", justifyContent: "center", height: "100%", p: 3 }}>
      <Paper variant="outlined" sx={{ maxWidth: 440, width: "100%", p: 4 }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 2 }}>
          <GitHubIcon />
          <Typography variant="h6">Connect your GitHub account</Typography>
        </Stack>
        <Typography color="textSecondary" sx={{ mb: 3 }}>
          {agentDisplayName} uses GitHub Copilot. Authenticate with your GitHub account — your own Copilot quota will be
          used.
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 0.5 }}>
          1. Open GitHub and enter the code below:
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontFamily: "monospace",
            letterSpacing: 4,
            textAlign: "center",
            mb: 3,
            bgcolor: "action.hover",
            borderRadius: 1,
            py: 1,
          }}
        >
          {auth.userCode}
        </Typography>
        {!opened ? (
          <>
            <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 2 }}>
              2. Click &quot;Open GitHub&quot; to authorize, then return here.
            </Typography>
            <Button
              variant="contained"
              fullWidth
              startIcon={<GitHubIcon />}
              href={auth.verificationUri}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                setPollMessage(undefined);
                setHasTerminalError(false);
                setOpened(true);
              }}
            >
              Open GitHub →
            </Button>
          </>
        ) : (
          <>
            <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 2 }}>
              {hasTerminalError
                ? "2. This GitHub authorization attempt cannot continue."
                : "2. Authorize in the GitHub tab, then return here — this page will update automatically."}
            </Typography>
            {!hasTerminalError && (
              <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", justifyContent: "center", py: 1.5 }}>
                <CircularProgress size={18} />
                <Typography color="textSecondary">Waiting for authorization…</Typography>
              </Stack>
            )}
            {pollMessage && (
              <Typography
                variant="caption"
                color={hasTerminalError ? "error" : "textSecondary"}
                sx={{ display: "block", textAlign: "center", mb: 1 }}
              >
                {pollMessage}
              </Typography>
            )}
            {hasTerminalError ? (
              <Button
                variant="contained"
                fullWidth
                sx={{ mt: 1 }}
                onClick={() => {
                  void restartAuth();
                }}
              >
                Start again
              </Button>
            ) : (
              <Button
                variant="text"
                fullWidth
                sx={{ mt: 1 }}
                href={auth.verificationUri}
                target="_blank"
                rel="noopener noreferrer"
              >
                Reopen GitHub tab
              </Button>
            )}
          </>
        )}
      </Paper>
    </Stack>
  );
};

export default GitHubAuthGate;
